import React, { useCallback, useEffect, useState } from "react";
import api from "utils/api";
import { adminBrandParams } from "utils/brand";
import { useTeamAdminBrand, useTeamAdminOrt } from "./TeamAdminBrandContext";
import { useTeamLocale } from "./TeamLocaleContext";
import { formatEuro, formatShortDate } from "./formatters";

const RechnungenTab = () => {
  const { t, dateLocale, locale } = useTeamLocale();
  const adminBrand = useTeamAdminBrand();
  const adminOrt = useTeamAdminOrt();
  const [entries, setEntries] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openingId, setOpeningId] = useState(null);

  const load = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get("/admin/rechnungen", {
          params: {
            ...adminBrandParams({ brand: adminBrand, ort: adminOrt }),
            page,
            limit: 25,
            q: query.trim(),
          },
        });
        setEntries(data.rechnungen || []);
        setPagination(data.pagination || { page: 1, pages: 1, total: 0 });
      } catch (err) {
        setError(err?.response?.data?.message || t("rechnungen.loadError"));
      } finally {
        setLoading(false);
      }
    },
    [adminBrand, adminOrt, query, t]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      load(1);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [load]);

  const openPdf = async (entry, { print = false } = {}) => {
    setOpeningId(entry._id);
    setError(null);
    try {
      const { data } = await api.get(`/admin/rechnungen/${entry._id}/pdf`, {
        params: adminBrandParams({ brand: adminBrand, ort: adminOrt }),
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const win = window.open(url, "_blank", "noopener");
      if (!win) {
        setError(t("rechnungen.popupBlocked"));
      } else if (print) {
        const triggerPrint = () => {
          try {
            win.focus();
            win.print();
          } catch {
            /* The PDF viewer still offers its own print action. */
          }
        };
        win.addEventListener("load", triggerPrint);
        window.setTimeout(triggerPrint, 700);
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setError(err?.response?.data?.message || t("rechnungen.openError"));
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <div className="team-manual team-rechnungen">
      <h2 className="team-manual-list-title">{t("rechnungen.title")}</h2>
      <p className="team-lead">{t("rechnungen.lead")}</p>
      <form
        className="team-rechnungen-search"
        onSubmit={(event) => {
          event.preventDefault();
          load(1);
        }}
      >
        <label htmlFor="rechnungen-search">{t("common.search")}</label>
        <input
          id="rechnungen-search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("rechnungen.searchPlaceholder")}
        />
      </form>

      {error && <div className="auth-alert is-error">{error}</div>}

      {loading ? (
        <div className="team-empty">{t("common.loading")}</div>
      ) : entries.length === 0 ? (
        <p className="team-muted">{t("rechnungen.empty")}</p>
      ) : (
        <>
          <div className="team-table-wrap">
            <table className="team-table">
              <thead>
                <tr>
                  <th>{t("rechnungen.colNumber")}</th>
                  <th>{t("rechnungen.colDate")}</th>
                  <th>{t("rechnungen.colCustomer")}</th>
                  <th>{t("rechnungen.colService")}</th>
                  <th>{t("rechnungen.colAmount")}</th>
                  <th>{t("rechnungen.colPayment")}</th>
                  <th aria-label={t("rechnungen.colActions")} />
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry._id}>
                    <td>{entry.invoiceNumber}</td>
                    <td>{formatShortDate(entry.issuedAt, dateLocale)}</td>
                    <td>
                      <div>{entry.recipientName || "—"}</div>
                      {entry.recipientCity ? (
                        <div className="team-muted">{entry.recipientCity}</div>
                      ) : null}
                    </td>
                    <td>{entry.serviceLabel || "—"}</td>
                    <td>{formatEuro(entry.grossCents, locale)}</td>
                    <td>{entry.paymentNote || "—"}</td>
                    <td className="team-table-actions">
                      <button
                        type="button"
                        className="team-secondary-btn"
                        disabled={openingId === entry._id}
                        onClick={() => openPdf(entry)}
                      >
                        {t("rechnungen.view")}
                      </button>
                      <button
                        type="button"
                        className="team-secondary-btn"
                        disabled={openingId === entry._id}
                        onClick={() => openPdf(entry, { print: true })}
                      >
                        {t("rechnungen.print")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div className="team-pagination">
              <button
                type="button"
                disabled={pagination.page <= 1}
                onClick={() => load(pagination.page - 1)}
              >
                {t("common.back")}
              </button>
              <span>
                {pagination.page} / {pagination.pages}
              </span>
              <button
                type="button"
                disabled={pagination.page >= pagination.pages}
                onClick={() => load(pagination.page + 1)}
              >
                {t("common.next")}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RechnungenTab;
